import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface FilterState {
  categories: string[];
  cuisines: string[];
  rating: number;
  features: string[];
  dishes: string[];
  essentials: string[];
  sortBy: string;
}

interface FilterStore extends FilterState {
  // Actions
  setCategories: (categories: string[]) => void;
  setCuisines: (cuisines: string[]) => void;
  setRating: (rating: number) => void;
  setFeatures: (features: string[]) => void;
  setDishes: (dishes: string[]) => void;
  setEssentials: (essentials: string[]) => void;
  setSortBy: (sortBy: string) => void;

  // Toggle actions for arrays
  toggleCategory: (category: string) => void;
  toggleCuisine: (cuisine: string) => void;
  toggleFeature: (feature: string) => void;
  toggleDish: (dish: string) => void;
  toggleEssential: (essential: string) => void;

  // Utility actions
  clearAllFilters: () => void;
  setAllFilters: (filters: FilterState) => void;

  // Get current filters as object
  getFilters: () => FilterState;
  
  // Get all collection handles from active filters
  getAllActiveCollectionHandles: () => string[];
}

const defaultFilters: FilterState = {
  categories: [],
  cuisines: [],
  rating: 0,
  features: [],
  dishes: [],
  essentials: [],
  sortBy: 'popularity',
};

export const useFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
      // Initial state
      ...defaultFilters,

      // Setters
      setCategories: categories => set({ categories }),
      setCuisines: cuisines => set({ cuisines }),
      setRating: rating => set({ rating }),
      setFeatures: features => set({ features }),
      setDishes: dishes => set({ dishes }),
      setEssentials: essentials => set({ essentials }),
      setSortBy: sortBy => set({ sortBy }),

      // Toggle functions
      toggleCategory: category =>
        set(state => ({
          categories: state.categories.includes(category)
            ? state.categories.filter(c => c !== category)
            : [...state.categories, category],
        })),

      toggleCuisine: cuisine =>
        set(state => ({
          cuisines: state.cuisines.includes(cuisine)
            ? state.cuisines.filter(c => c !== cuisine)
            : [...state.cuisines, cuisine],
        })),

      toggleFeature: feature =>
        set(state => ({
          features: state.features.includes(feature)
            ? state.features.filter(f => f !== feature)
            : [...state.features, feature],
        })),

      toggleDish: dish =>
        set(state => ({
          dishes: state.dishes.includes(dish)
            ? state.dishes.filter(d => d !== dish)
            : [...state.dishes, dish],
        })),

      toggleEssential: essential =>
        set(state => ({
          essentials: state.essentials.includes(essential)
            ? state.essentials.filter(e => e !== essential)
            : [...state.essentials, essential],
        })),

      // Utility functions
      clearAllFilters: () => set(defaultFilters),

      setAllFilters: filters => set(filters),

      getFilters: () => {
        const state = get();
        return {
          categories: state.categories,
          cuisines: state.cuisines,
          rating: state.rating,
          features: state.features,
          dishes: state.dishes,
          essentials: state.essentials,
          sortBy: state.sortBy,
        };
      },

      getAllActiveCollectionHandles: () => {
        const state = get();
        const allHandles: string[] = [];
        
        // Categories - map to collection handles
        state.categories.forEach(category => {
          const categoryCollectionMap: Record<string, string[]> = {
            'popular-dishes': ['for-pizza', 'for-pasta', 'for-steak', 'for-chicken'],
            'kitchen-essentials': ['kitchen-essentials', 'spices-and-herbs'],
            'spice-blends': ['spice-blends', 'regional-blends'],
            'hot-peppers-chili': ['hot-peppers-and-chili'],
            'paprika': ['paprika'],
            'organic': ['organic-spices'],
            'grill-bbq': ['for-grill-bbq'],
            'gift-sets': ['gift-sets'],
            'flavored-salts': ['flavored-salts'],
            'world-cuisines': ['italian-cuisine', 'indian-cuisine', 'asian-cuisine', 'mediterranean-cuisine'],
            'dish-type': ['meat-dishes', 'fish-and-seafood', 'vegetable-dishes'],
            'baking': ['for-baking'],
            'sales-discounts': ['sales-discounts']
          };
          allHandles.push(...(categoryCollectionMap[category] || []));
        });
        
        // Cuisines - map to collection handles
        state.cuisines.forEach(cuisine => {
          const cuisineCollectionMap: Record<string, string[]> = {
            indian: ['indian-cuisine'],
            mediterranean: ['mediterranean-cuisine'],
            asian: ['asian-cuisine'], 
            mexican: ['mexican-cuisine'],
            italian: ['italian-cuisine'],
            'middle-eastern': ['middle-eastern-cuisine'],
          };
          allHandles.push(...(cuisineCollectionMap[cuisine] || []));
        });
        
        // Dishes - map to collection handles
        state.dishes.forEach(dish => {
          const dishCollectionMap: Record<string, string[]> = {
            pizza: ['for-pizza'],
            pasta: ['for-pasta'],
            steak: ['for-steak'],
            chicken: ['for-chicken', 'for-fried-chicken'],
            fish: ['fish-and-seafood', 'for-grilled-fish'],
            vegetables: ['for-grilled-vegetables', 'for-roasted-potatoes'],
            soups: ['for-soups'],
            bbq: ['for-grill-bbq', 'for-kebab-bbq-skewers'],
          };
          allHandles.push(...(dishCollectionMap[dish] || []));
        });
        
        // Essentials - map to collection handles
        state.essentials.forEach(essential => {
          const essentialCollectionMap: Record<string, string[]> = {
            'basic-spices': ['spices-and-herbs', 'kitchen-essentials'],
            'essential-herbs': ['spices-and-herbs', 'herbs'],
            'everyday-blends': ['spice-blends', 'ready-made-blends'],
            'cooking-salts': ['salts', 'himalayan-salts'],
            'starter-kit': ['gift-sets', 'starter-collections'],
          };
          allHandles.push(...(essentialCollectionMap[essential] || []));
        });
        
        // Features - map to collection handles
        state.features.forEach(feature => {
          const featureCollectionMap: Record<string, string[]> = {
            organic: ['organic-spices'],
            hot: ['hot-peppers-and-chili'],
            mild: ['sweet-and-mild-0-1000-scoville'],
            premium: ['premium-spices'],
          };
          allHandles.push(...(featureCollectionMap[feature] || []));
        });
        
        // Remove duplicates and return
        return [...new Set(allHandles)];
      },
    }),
    {
      name: 'filter-store', // unique name for localStorage
      // Only persist filters, don't persist functions
      partialize: state => ({
        categories: state.categories,
        cuisines: state.cuisines,
        rating: state.rating,
        features: state.features,
        dishes: state.dishes,
        essentials: state.essentials,
        sortBy: state.sortBy,
      }),
    }
  )
);
